// jve-2-4: OAuth2 authorization-code + PKCE helper (FR29). The SPA is a PUBLIC client — no client
// secret in the bundle — so PKCE (RFC 7636, S256) protects the code exchange. This module is pure +
// deterministic (given a verifier) so it is fully unit-testable WITHOUT a live OIDC issuer; the live
// browser redirect/e2e is jve-2-2 (gated on a live issuer — external blocker).

export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

const PKCE_VERIFIER_KEY = "fawkes.pkce.verifier";
const PKCE_STATE_KEY = "fawkes.pkce.state";

/**
 * Read OIDC config from Vite env (`VITE_OIDC_*`). Returns undefined when not configured — the
 * authProvider then surfaces a clear "not configured" result instead of attempting a redirect.
 */
export function readOidcConfig(): OidcConfig | undefined {
  const env = import.meta.env as Record<string, string | undefined>;
  const issuer = env.VITE_OIDC_ISSUER;
  const clientId = env.VITE_OIDC_CLIENT_ID;
  if (!issuer || !clientId) {
    return undefined;
  }
  return {
    issuer: issuer.replace(/\/$/, ""),
    clientId,
    redirectUri: env.VITE_OIDC_REDIRECT_URI ?? `${globalThis.location?.origin ?? ""}/`,
    scope: env.VITE_OIDC_SCOPE ?? "openid profile",
  };
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A high-entropy code verifier (RFC 7636 §4.1 — 43–128 chars of the unreserved set). */
export function generateCodeVerifier(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** S256 challenge: base64url(SHA-256(verifier)) (RFC 7636 §4.2). */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/** Build the authorization-endpoint URL for the redirect (response_type=code, S256). */
export function buildAuthorizationUrl(config: OidcConfig, codeChallenge: string, state: string): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  return `${config.issuer}/oauth2/authorize?${params.toString()}`;
}

export interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
}

/** Exchange the authorization code for tokens at the token endpoint (PKCE, no client secret). */
export async function exchangeCodeForToken(
  config: OidcConfig,
  code: string,
  codeVerifier: string,
): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: codeVerifier,
  });
  const response = await fetch(`${config.issuer}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error(`token exchange failed: ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
}

// The PKCE verifier + state are short-lived correlators for the round-trip, NOT credentials, so
// sessionStorage is appropriate (they survive the authorization redirect). They are cleared right
// after the exchange. The ACCESS TOKEN is the secret and never touches web storage (tokenStore).
export function rememberPkceState(verifier: string, state: string): void {
  globalThis.sessionStorage?.setItem(PKCE_VERIFIER_KEY, verifier);
  globalThis.sessionStorage?.setItem(PKCE_STATE_KEY, state);
}

export function consumePkceVerifier(state: string): string | undefined {
  const expected = globalThis.sessionStorage?.getItem(PKCE_STATE_KEY);
  const verifier = globalThis.sessionStorage?.getItem(PKCE_VERIFIER_KEY) ?? undefined;
  globalThis.sessionStorage?.removeItem(PKCE_VERIFIER_KEY);
  globalThis.sessionStorage?.removeItem(PKCE_STATE_KEY);
  if (!expected || expected !== state) {
    return undefined; // state mismatch → CSRF guard
  }
  return verifier;
}
